import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    Caption,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import type {Role} from "@khan/user-settings";

import StudentSettingsCheckbox from "#class-package/student/student-settings-checkbox.tsx";
import {useApolloGqlClient} from "#data-access/gql/use-apollo-gql-client.ts";

import {
    updateRolesAndHomepage,
    type UserHomepage,
    type UserRole,
} from "./roles.ts";

type Props = {
    canAccessDistrictsHomepage: boolean;
    userChosenRoles: Array<Role>;
    isEligibleToBeParent: boolean;
    successfullyChangedSettings: () => unknown;
    homepage: string;
};

type State = {
    isUnexpectedError: boolean;
    roleTeacherChecked: boolean;
    roleParentChecked: boolean;
    selectedHomepage: string;
    isLoading: boolean;
};

export default function RolesSection(props: Props): React.ReactNode {
    const {
        canAccessDistrictsHomepage,
        userChosenRoles,
        isEligibleToBeParent,
        successfullyChangedSettings,
        homepage,
    } = props;

    const {gqlClient} = useApolloGqlClient();

    const [state, setState] = React.useState<State>({
        isUnexpectedError: false,
        roleTeacherChecked:
            userChosenRoles.includes("teacher") ||
            userChosenRoles.includes("inferred_teacher"),
        roleParentChecked:
            userChosenRoles.includes("parent") ||
            userChosenRoles.includes("inferred_parent"),
        selectedHomepage: homepage,
        isLoading: false,
    });

    const {
        isUnexpectedError,
        roleTeacherChecked,
        roleParentChecked,
        selectedHomepage,
        isLoading,
    } = state;

    const settingsHaveNotChangedFromProps = (): boolean => {
        const teacherRoleIsSame =
            roleTeacherChecked ===
            (userChosenRoles.includes("teacher") ||
                userChosenRoles.includes("inferred_teacher"));
        const parentRoleIsSame =
            roleParentChecked ===
            (userChosenRoles.includes("parent") ||
                userChosenRoles.includes("inferred_parent"));
        return (
            teacherRoleIsSame &&
            parentRoleIsSame &&
            selectedHomepage === homepage
        );
    };

    const submitForm = () => {
        setState({
            ...state,
            isUnexpectedError: false,
            isLoading: true,
        });

        const selectedRoles: Array<UserRole> = [];
        if (roleTeacherChecked) {
            selectedRoles.push("TEACHER");
        }

        if (roleParentChecked) {
            selectedRoles.push("PARENT");
        }

        const homepageMap: Record<string, UserHomepage> = {
            learner: "LEARNER",
            parent: "PARENT",
            coach: "COACH",
            districtadmin: "DISTRICT_ADMIN",
        };

        const gqlHomepage = homepageMap[selectedHomepage] || "LEARNER";

        updateRolesAndHomepage(gqlClient, selectedRoles, gqlHomepage)
            .then((result) => {
                successfullyChangedSettings();
                setState({...state, isLoading: false});
            })
            .catch((error) => {
                setState({
                    ...state,
                    isUnexpectedError: true,
                    isLoading: false,
                });
            });
    };

    return (
        <View role="region" aria-label={t`Role settings`}>
            <HeadingSmall style={styles.header} tag="h2">
                {t`Roles`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            <Body>{t`I am using Khan Academy as a:`}</Body>
            <Strut size={spacing.medium_16} />
            <form onSubmit={submitForm}>
                <View>
                    {/* District admin role cannot be changed from settings page. */}
                    {canAccessDistrictsHomepage && (
                        <React.Fragment>
                            <StudentSettingsCheckbox
                                label={t`Administrator`}
                                id="role-district-admin"
                                isChecked={true}
                                disabled={true}
                                onChange={() => {}}
                                description={t`Your account is synced with a district. To unsync, contact Khan Academy support.`}
                            />
                            <Strut size={spacing.large_24} />
                        </React.Fragment>
                    )}

                    {/* All users have the learner role checked and cannot be changed. */}
                    <StudentSettingsCheckbox
                        label={t`Learner`}
                        id="role-learner"
                        isChecked={true}
                        disabled={true}
                        onChange={() => {}}
                        description={t`Learner mode is enabled for all users!`}
                    />
                    <Strut size={spacing.large_24} />

                    <StudentSettingsCheckbox
                        label={t`Teacher/Coach`}
                        id="role-teacher"
                        isChecked={roleTeacherChecked}
                        disabled={selectedHomepage === "coach"}
                        onChange={(roleTeacherChecked) =>
                            setState({...state, roleTeacherChecked})
                        }
                        description={
                            selectedHomepage === "coach"
                                ? t`To disable this role, please change your homepage.`
                                : undefined
                        }
                    />
                    <Strut size={spacing.large_24} />

                    {isEligibleToBeParent && (
                        <>
                            <StudentSettingsCheckbox
                                label={t`Parent`}
                                id="role-parent"
                                isChecked={roleParentChecked}
                                disabled={selectedHomepage === "parent"}
                                onChange={(roleParentChecked) =>
                                    setState({
                                        ...state,
                                        roleParentChecked,
                                    })
                                }
                                description={
                                    selectedHomepage === "parent"
                                        ? t`To disable this role, please change your homepage.`
                                        : undefined
                                }
                            />
                            <Strut size={spacing.large_24} />
                        </>
                    )}

                    <Body>{t`Which page do you want as your homepage?`}</Body>
                    <Strut size={spacing.medium_16} />

                    <View style={styles.row}>
                        <LabelMedium
                            style={[styles.verticalCenter, styles.label]}
                            tag="label"
                            htmlFor="homepage"
                        >
                            {t`Homepage`}
                        </LabelMedium>
                        <Strut size={spacing.medium_16} />

                        <SingleSelect
                            id="homepage"
                            testId="homepage-select"
                            placeholder=""
                            onChange={(selectedHomepage) =>
                                setState({...state, selectedHomepage})
                            }
                            selectedValue={selectedHomepage}
                        >
                            <OptionItem label={t`Learner`} value="learner" />
                            {roleParentChecked && (
                                <OptionItem
                                    label={t`Parent dashboard`}
                                    value="parent"
                                />
                            )}

                            {roleTeacherChecked && (
                                <OptionItem
                                    label={t`Teacher dashboard`}
                                    value="coach"
                                />
                            )}
                            {canAccessDistrictsHomepage && (
                                <OptionItem
                                    label={t`Administrator dashboard`}
                                    value="districtadmin"
                                />
                            )}
                        </SingleSelect>
                    </View>
                    <Strut size={spacing.large_24} />
                    <Button
                        type="submit"
                        style={styles.button}
                        disabled={settingsHaveNotChangedFromProps()}
                        spinner={isLoading}
                    >
                        {t`Save changes`}
                    </Button>
                    {isUnexpectedError && (
                        <Caption style={styles.error}>
                            {t`Oops. Something went wrong. Please try again.`}
                        </Caption>
                    )}
                </View>
            </form>
        </View>
    );
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    error: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    row: {
        flexDirection: "row",
    },
    label: {
        color: color.offBlack,
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
});
